import { useState, useEffect } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useAuth } from '../contexts/AuthContext'
import { adminAPI } from '../services/api'
import { ADMIN_SECTIONS, adminSectionFrom } from '../components/adminSections'
import PageHeader from '../components/PageHeader'
import 'bootstrap-icons/font/bootstrap-icons.css'

const Admin = () => {
  const { isSuperuser } = useAuth()
  // Section comes from the URL (/admin?tab=clients) so the sidebar and a page reload agree
  const [searchParams, setSearchParams] = useSearchParams()
  const activeTab = adminSectionFrom(searchParams)
  const setActiveTab = (id) => setSearchParams({ tab: id })

  // Keep the selected section visible in the mobile picker
  useEffect(() => {
    document.querySelector('[data-admin-chip-active]')?.scrollIntoView({ inline: 'center', block: 'nearest' })
  }, [activeTab])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  
  // Data states for each model
  const [users, setUsers] = useState([])
  const [surveyors, setSurveyors] = useState([])
  const [clients, setClients] = useState([])
  const [countries, setCountries] = useState([])
  const [ports, setPorts] = useState([])
  const [surveyTypes, setSurveyTypes] = useState([])
  const [operations, setOperations] = useState([])
  const [events, setEvents] = useState([])
  
  // Modal states
  const [showModal, setShowModal] = useState(false)
  const [modalMode, setModalMode] = useState('create') // 'create' or 'edit'
  const [currentItem, setCurrentItem] = useState(null)
  const [formData, setFormData] = useState({})

  useEffect(() => {
    if (!isSuperuser) {
      window.location.href = '/'
      return
    }
    loadData(activeTab)
  }, [activeTab, isSuperuser])

  const loadData = async (tab) => {
    setLoading(true)
    setError(null)
    try {
      switch(tab) {
        case 'users': {
          const usersRes = await adminAPI.getUsers()
          setUsers(usersRes.data.data || [])
          break
        }
        case 'surveyors': {
          const surveyorsRes = await adminAPI.getSurveyors()
          setSurveyors(surveyorsRes.data.data || [])
          break
        }
        case 'clients': {
          const clientsRes = await adminAPI.getClients()
          setClients(clientsRes.data.data || [])
          break
        }
        case 'countries': {
          const countriesRes = await adminAPI.getCountries()
          setCountries(countriesRes.data.data || [])
          break
        }
        case 'ports': {
          const portsRes = await adminAPI.getPorts()
          setPorts(portsRes.data.data || [])
          // Also load countries for port form
          const countriesForPorts = await adminAPI.getCountries()
          setCountries(countriesForPorts.data.data || [])
          break
        }
        case 'survey-types': {
          const typesRes = await adminAPI.getSurveyTypes()
          setSurveyTypes(typesRes.data.data || [])
          break
        }
        case 'operations': {
          const opsRes = await adminAPI.getOperations()
          setOperations(opsRes.data.data || [])
          break
        }
        case 'events': {
          const eventsRes = await adminAPI.getEvents()
          setEvents(eventsRes.data.data || [])
          break
        }
        default:
          break
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load data')
      console.error('Error loading data:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleCreate = () => {
    setModalMode('create')
    setCurrentItem(null)
    setFormData({})
    setShowModal(true)
  }

  const handleEdit = (item) => {
    setModalMode('edit')
    setCurrentItem(item)
    // Handle country field for ports - convert object to ID if needed
    const formDataCopy = { ...item }
    if (activeTab === 'ports' && formDataCopy.country && typeof formDataCopy.country === 'object') {
      formDataCopy.country = formDataCopy.country.id
    }
    setFormData(formDataCopy)
    setShowModal(true)
  }

  const handleDelete = async (id, type) => {
    if (!window.confirm(`Are you sure you want to delete this ${type}?`)) return
    
    try {
      switch(type) {
        case 'user':
          await adminAPI.deleteUser(id)
          break
        case 'surveyor':
          await adminAPI.deleteSurveyor(id)
          break
        case 'client':
          await adminAPI.deleteClient(id)
          break
        case 'country':
          await adminAPI.deleteCountry(id)
          break
        case 'port':
          await adminAPI.deletePort(id)
          break
        case 'survey-type':
          await adminAPI.deleteSurveyType(id)
          break
        case 'operation':
          await adminAPI.deleteOperation(id)
          break
        case 'event':
          await adminAPI.deleteEvent(id)
          break
        default:
          break
      }
      // Reload data after successful delete
      await loadData(activeTab)
    } catch (err) {
      const errorMessage = err.response?.data?.message || 
                          err.response?.data?.detail || 
                          err.message || 
                          `Failed to delete ${type}`
      alert(errorMessage)
      console.error('Delete error:', err)
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setLoading(true)
    setError(null)
    
    try {
      switch(activeTab) {
        case 'users':
          if (modalMode === 'create') {
            await adminAPI.createUser(formData)
          } else {
            await adminAPI.updateUser(currentItem.id, formData)
          }
          break
        case 'surveyors':
          if (modalMode === 'create') {
            await adminAPI.createSurveyor(formData)
          } else {
            await adminAPI.updateSurveyor(currentItem.code, formData)
          }
          break
        case 'clients':
          if (modalMode === 'create') {
            await adminAPI.createClient(formData)
          } else {
            await adminAPI.updateClient(currentItem.id, formData)
          }
          break
        case 'countries':
          if (modalMode === 'create') {
            await adminAPI.createCountry(formData)
          } else {
            await adminAPI.updateCountry(currentItem.id, formData)
          }
          break
        case 'ports':
          if (modalMode === 'create') {
            await adminAPI.createPort(formData)
          } else {
            await adminAPI.updatePort(currentItem.id, formData)
          }
          break
        case 'survey-types':
          if (modalMode === 'create') {
            await adminAPI.createSurveyType(formData)
          } else {
            await adminAPI.updateSurveyType(currentItem.name, formData)
          }
          break
        case 'operations':
          if (modalMode === 'create') {
            await adminAPI.createOperation(formData)
          } else {
            await adminAPI.updateOperation(currentItem.id, formData)
          }
          break
        case 'events':
          if (modalMode === 'create') {
            await adminAPI.createEvent(formData)
          } else {
            await adminAPI.updateEvent(currentItem.id, formData)
          }
          break
        default:
          break
      }
      
      setShowModal(false)
      await loadData(activeTab)
    } catch (err) {
      setError(err.response?.data?.message || err.response?.data?.errors || 'Failed to save')
      console.error('Error saving:', err)
    } finally {
      setLoading(false)
    }
  }

  const tabs = ADMIN_SECTIONS

  const renderTable = () => {
    const data = {
      users,
      surveyors,
      clients,
      countries,
      ports,
      'survey-types': surveyTypes,
      operations,
      events,
    }[activeTab] || []

    switch(activeTab) {
      case 'users':
        return (
          <table className="w-full">
            <thead>
              <tr className="table-head-premium">
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Username</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Welcome Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Staff</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Superuser</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Clientuser</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map(user => (
                <tr key={user.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">{user.username}</td>
                  <td className="px-4 py-3 text-sm">{user.email || '-'}</td>
                  <td className="px-4 py-3 text-sm">{`${user.first_name || ''} ${user.last_name || ''}`.trim() || '-'}</td>
                  <td className="px-4 py-3 text-sm">{user.welcome_name || '-'}</td>
                  <td className="px-4 py-3 text-sm">{user.is_staff ? '✓' : '✗'}</td>
                  <td className="px-4 py-3 text-sm">{user.is_superuser ? '✓' : '✗'}</td>
                  <td className="px-4 py-3 text-sm">{user.category === 'clientuser' ? '✓' : '✗'}</td>
                  <td className="px-4 py-3 text-sm">
                    <button onClick={() => handleEdit(user)} className="text-[#ea580c] hover:text-[#9a3412] mr-3">
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button onClick={() => handleDelete(user.id, 'user')} className="text-red-600 hover:text-red-800">
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      case 'surveyors':
        return (
          <table className="w-full">
            <thead>
              <tr className="table-head-premium">
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Code</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Phone</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Type</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Active</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map(surveyor => (
                <tr key={surveyor.code} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{surveyor.code}</td>
                  <td className="px-4 py-3 text-sm">{`${surveyor.first_name || ''} ${surveyor.last_name || ''}`.trim() || '-'}</td>
                  <td className="px-4 py-3 text-sm">{surveyor.email || '-'}</td>
                  <td className="px-4 py-3 text-sm">{surveyor.phone || '-'}</td>
                  <td className="px-4 py-3 text-sm">{surveyor.surveyor_type || '-'}</td>
                  <td className="px-4 py-3 text-sm">{surveyor.is_active ? '✓' : '✗'}</td>
                  <td className="px-4 py-3 text-sm">
                    <button onClick={() => handleEdit(surveyor)} className="text-[#ea580c] hover:text-[#9a3412] mr-3">
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button onClick={() => handleDelete(surveyor.code, 'surveyor')} className="text-red-600 hover:text-red-800">
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      case 'clients':
        return (
          <table className="w-full">
            <thead>
              <tr className="table-head-premium">
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Username</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Email</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Address</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map(client => (
                <tr key={client.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{client.name}</td>
                  <td className="px-4 py-3 text-sm">{client.username || '-'}</td>
                  <td className="px-4 py-3 text-sm">{client.email || '-'}</td>
                  <td className="px-4 py-3 text-sm">{client.address || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <button onClick={() => handleEdit(client)} className="text-[#ea580c] hover:text-[#9a3412] mr-3">
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button onClick={() => handleDelete(client.id, 'client')} className="text-red-600 hover:text-red-800">
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      case 'countries':
        return (
          <table className="w-full">
            <thead>
              <tr className="table-head-premium">
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map(country => (
                <tr key={country.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{country.name}</td>
                  <td className="px-4 py-3 text-sm">
                    <button onClick={() => handleEdit(country)} className="text-[#ea580c] hover:text-[#9a3412] mr-3">
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button onClick={() => handleDelete(country.id, 'country')} className="text-red-600 hover:text-red-800">
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      case 'ports':
        return (
          <table className="w-full">
            <thead>
              <tr className="table-head-premium">
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Country</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map(port => (
                <tr key={port.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{port.name}</td>
                  <td className="px-4 py-3 text-sm">{port.country_name || (typeof port.country === 'object' ? port.country?.name : port.country) || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <button onClick={() => handleEdit(port)} className="text-[#ea580c] hover:text-[#9a3412] mr-3">
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button onClick={() => handleDelete(port.id, 'port')} className="text-red-600 hover:text-red-800">
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      case 'survey-types':
        return (
          <table className="w-full">
            <thead>
              <tr className="table-head-premium">
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Name</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map(type => (
                <tr key={type.name} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{type.name}</td>
                  <td className="px-4 py-3 text-sm">
                    <button onClick={() => handleEdit(type)} className="text-[#ea580c] hover:text-[#9a3412] mr-3">
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button onClick={() => handleDelete(type.name, 'survey-type')} className="text-red-600 hover:text-red-800">
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      case 'operations':
        return (
          <table className="w-full">
            <thead>
              <tr className="table-head-premium">
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Title</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Start Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">End Date</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Survey Ref</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map(op => (
                <tr key={op.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{op.title}</td>
                  <td className="px-4 py-3 text-sm">{op.start_date}</td>
                  <td className="px-4 py-3 text-sm">{op.end_date}</td>
                  <td className="px-4 py-3 text-sm">{op.survey_ref || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <button onClick={() => handleEdit(op)} className="text-[#ea580c] hover:text-[#9a3412] mr-3">
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button onClick={() => handleDelete(op.id, 'operation')} className="text-red-600 hover:text-red-800">
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      case 'events':
        return (
          <table className="w-full">
            <thead>
              <tr className="table-head-premium">
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Title</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Start</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">End</th>
                <th className="px-4 py-3 text-left text-sm font-semibold text-white">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.map(event => (
                <tr key={event.id} className="border-b border-gray-100 hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-medium">{event.title}</td>
                  <td className="px-4 py-3 text-sm">{event.start ? new Date(event.start).toLocaleString() : '-'}</td>
                  <td className="px-4 py-3 text-sm">{event.end ? new Date(event.end).toLocaleString() : '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <button onClick={() => handleEdit(event)} className="text-[#ea580c] hover:text-[#9a3412] mr-3">
                      <i className="bi bi-pencil"></i>
                    </button>
                    <button onClick={() => handleDelete(event.id, 'event')} className="text-red-600 hover:text-red-800">
                      <i className="bi bi-trash"></i>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )
      default:
        return <div>No data</div>
    }
  }

  const renderForm = () => {
    switch(activeTab) {
      case 'users':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Username *</label>
              <input
                type="text"
                value={formData.username || ''}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                required
                disabled={modalMode === 'edit'}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">First Name</label>
              <input
                type="text"
                value={formData.first_name || ''}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Last Name</label>
              <input
                type="text"
                value={formData.last_name || ''}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Welcome Name</label>
              <input
                type="text"
                value={formData.welcome_name || ''}
                onChange={(e) => setFormData({...formData, welcome_name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                placeholder="Custom welcome name to display"
              />
              <p className="text-xs text-gray-500 mt-1">This name will be displayed in welcome messages instead of the user's first/last name</p>
            </div>
            {modalMode === 'create' && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">Password *</label>
                <input
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                  required={modalMode === 'create'}
                />
              </div>
            )}
            {modalMode === 'edit' && (
              <div className="mb-4">
                <label className="block text-sm font-medium mb-1">New Password (leave blank to keep current)</label>
                <input
                  type="password"
                  value={formData.password || ''}
                  onChange={(e) => setFormData({...formData, password: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                />
              </div>
            )}
            <div className="mb-4 flex items-center gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_staff || false}
                  onChange={(e) => setFormData({...formData, is_staff: e.target.checked})}
                  className="mr-2"
                />
                Is Staff
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_superuser || false}
                  onChange={(e) => setFormData({...formData, is_superuser: e.target.checked})}
                  className="mr-2"
                />
                Is Superuser
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.category === 'clientuser' || false}
                  onChange={(e) => setFormData({...formData, category: e.target.checked ? 'clientuser' : ''})}
                  className="mr-2"
                />
                Is Clientuser
              </label>
            </div>
          </>
        )
      case 'surveyors':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Code *</label>
              <input
                type="text"
                value={formData.code || ''}
                onChange={(e) => setFormData({...formData, code: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                required
                disabled={modalMode === 'edit'}
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">First Name</label>
              <input
                type="text"
                value={formData.first_name || ''}
                onChange={(e) => setFormData({...formData, first_name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Last Name</label>
              <input
                type="text"
                value={formData.last_name || ''}
                onChange={(e) => setFormData({...formData, last_name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Email</label>
              <input
                type="email"
                value={formData.email || ''}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Phone</label>
              <input
                type="text"
                value={formData.phone || ''}
                onChange={(e) => setFormData({...formData, phone: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Surveyor Type</label>
              <select
                value={formData.surveyor_type || 'In-House'}
                onChange={(e) => setFormData({...formData, surveyor_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
              >
                <option value="In-House">In-House</option>
                <option value="Subcont">Subcont</option>
              </select>
            </div>
            <div className="mb-4 flex items-center gap-4">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_active !== false}
                  onChange={(e) => setFormData({...formData, is_active: e.target.checked})}
                  className="mr-2"
                />
                Is Active
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_staff || false}
                  onChange={(e) => setFormData({...formData, is_staff: e.target.checked})}
                  className="mr-2"
                />
                Is Staff
              </label>
              <label className="flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_superuser || false}
                  onChange={(e) => setFormData({...formData, is_superuser: e.target.checked})}
                  className="mr-2"
                />
                Is Superuser
              </label>
            </div>
          </>
        )
      case 'clients':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Username</label>
              <input
                type="text"
                value={formData.username || ''}
                onChange={(e) => setFormData({...formData, username: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Email</label>
              <textarea
                value={formData.email || ''}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                rows="3"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Address</label>
              <textarea
                value={formData.address || ''}
                onChange={(e) => setFormData({...formData, address: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                rows="3"
              />
            </div>
          </>
        )
      case 'countries':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                required
              />
            </div>
          </>
        )
      case 'ports':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Country *</label>
              <select
                value={formData.country || (typeof formData.country === 'object' ? formData.country?.id : formData.country) || ''}
                onChange={(e) => setFormData({...formData, country: parseInt(e.target.value)})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                required
              >
                <option value="">Select Country</option>
                {countries.map(country => (
                  <option key={country.id} value={country.id}>{country.name}</option>
                ))}
              </select>
            </div>
          </>
        )
      case 'survey-types':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Name *</label>
              <input
                type="text"
                value={formData.name || ''}
                onChange={(e) => setFormData({...formData, name: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                required
                disabled={modalMode === 'edit'}
              />
            </div>
          </>
        )
      case 'operations':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Title *</label>
              <input
                type="text"
                value={formData.title || ''}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Start Date *</label>
              <input
                type="date"
                value={formData.start_date || ''}
                onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">End Date *</label>
              <input
                type="date"
                value={formData.end_date || ''}
                onChange={(e) => setFormData({...formData, end_date: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                rows="3"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Color</label>
              <select
                value={formData.color || 'orange'}
                onChange={(e) => setFormData({...formData, color: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
              >
                <option value="orange">Orange</option>
                <option value="red">Red</option>
                <option value="green">Green</option>
                <option value="blue">Blue</option>
                <option value="purple">Purple</option>
              </select>
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Survey Type</label>
              <input
                type="text"
                value={formData.survey_type || ''}
                onChange={(e) => setFormData({...formData, survey_type: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
              />
            </div>
          </>
        )
      case 'events':
        return (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Title *</label>
              <input
                type="text"
                value={formData.title || ''}
                onChange={(e) => setFormData({...formData, title: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Start *</label>
              <input
                type="datetime-local"
                value={formData.start ? (typeof formData.start === 'string' ? formData.start.slice(0, 16) : new Date(formData.start).toISOString().slice(0, 16)) : ''}
                onChange={(e) => setFormData({...formData, start: e.target.value ? new Date(e.target.value).toISOString() : null})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                required
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">End</label>
              <input
                type="datetime-local"
                value={formData.end ? (typeof formData.end === 'string' ? formData.end.slice(0, 16) : new Date(formData.end).toISOString().slice(0, 16)) : ''}
                onChange={(e) => setFormData({...formData, end: e.target.value ? new Date(e.target.value).toISOString() : null})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Description</label>
              <textarea
                value={formData.description || ''}
                onChange={(e) => setFormData({...formData, description: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
                rows="3"
              />
            </div>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1">Color</label>
              <input
                type="text"
                value={formData.color || 'orange'}
                onChange={(e) => setFormData({...formData, color: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-200 focus:border-[#ea580c]"
              />
            </div>
          </>
        )
      default:
        return null
    }
  }

  if (!isSuperuser) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h1>
          <p className="text-gray-600">You must be a superuser to access this page.</p>
        </div>
      </div>
    )
  }

  const currentData = {
    users,
    surveyors,
    clients,
    countries,
    ports,
    'survey-types': surveyTypes,
    operations,
    events,
  }[activeTab] || []

  return (
    <div className="page-container">
      <PageHeader title="KBL Admin Panel" description="Manage users, reference data and calendar records" />

      {/* Section picker for small screens; on desktop the admin sub-sidebar does this */}
      <div className="-mx-1 mb-6 flex gap-2 overflow-x-auto px-1 pb-1 md:hidden [scrollbar-width:none]">
        {tabs.map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            type="button"
            onClick={() => setActiveTab(id)}
            data-admin-chip-active={activeTab === id ? '' : undefined}
            className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-3.5 py-2 text-sm font-medium transition-colors ${
              activeTab === id
                ? 'border-[#ea580c] bg-[#fff7ed] text-[#9a3412]'
                : 'border-[#efe3d6] bg-white text-slate-600 hover:border-[#fdba74]'
            }`}
          >
            <Icon className="h-4 w-4" strokeWidth={2} />
            {label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="card-premium p-5 md:p-6">
        <div className="flex flex-wrap justify-between items-center gap-3 mb-5">
          <h2 className="flex items-center gap-2.5 text-lg font-semibold text-slate-900">
            {(() => {
              const Icon = tabs.find(t => t.id === activeTab)?.icon
              return Icon ? <Icon className="h-5 w-5 text-[#ea580c]" strokeWidth={2} /> : null
            })()}
            {tabs.find(t => t.id === activeTab)?.label}
            <span className="rounded-full bg-[#fff7ed] px-2.5 py-0.5 text-xs font-semibold tabular-nums text-[#9a3412]">{currentData.length}</span>
          </h2>
          <button
            onClick={handleCreate}
            className="px-4 py-2 text-sm bg-premium text-white font-semibold rounded-lg shadow-[0_2px_8px_rgba(234,88,12,0.3)] hover:shadow-[0_4px_14px_rgba(234,88,12,0.4)] transition-shadow flex items-center gap-2 shrink-0"
          >
            <i className="bi bi-plus-lg"></i>
            Add New
          </button>
        </div>

        {loading && !showModal ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-kbl-neon-500"></div>
          </div>
        ) : error && !showModal ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        ) : currentData.length === 0 ? (
          <div className="text-center py-12 text-gray-500">
            No {tabs.find(t => t.id === activeTab)?.label.toLowerCase()} found
          </div>
        ) : (
          <div className="overflow-x-auto">
            {renderTable()}
          </div>
        )}
      </div>

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 z-10 bg-premium px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-white">
                {modalMode === 'create' ? 'Create' : 'Edit'} {tabs.find(t => t.id === activeTab)?.singular}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="rounded-lg p-1 text-white/90 hover:bg-white/20 hover:text-white"
              >
                <i className="bi bi-x-lg text-xl"></i>
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-6">
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 mb-4">
                  {typeof error === 'object' ? JSON.stringify(error) : error}
                </div>
              )}
              {renderForm()}
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="px-6 py-2 bg-premium text-white font-semibold rounded-lg shadow-[0_2px_8px_rgba(234,88,12,0.3)] hover:shadow-[0_4px_14px_rgba(234,88,12,0.4)] transition-shadow disabled:opacity-50"
                >
                  {loading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Admin

